import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Trash2, RotateCcw } from 'lucide-react'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import {
  ListingDataTableCard,
  ListingDataTableScroll,
  LISTING_DATA_TABLE_BODY_CLASS,
  LISTING_DATA_TABLE_CLASS,
  LISTING_DATA_TABLE_HEAD_CLASS,
} from '@/components/ui/listingDataTable'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'
import { deleteFailedJob, getFailedJobs, retryFailedJob } from '../services/jobsService'
import type { FailedJob } from '../types'

function formatDateTime(value: string | number | undefined | null): string {
  if (!value) return ''
  const ts = typeof value === 'number' ? value * 1000 : Date.parse(value)
  const date = new Date(ts)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

const jobToolbarClassName =
  'inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none rounded transition-colors'

export function FailedJobsPage() {
  const [jobs, setJobs] = useState<FailedJob[]>([])
  const [loading, setLoading] = useState(false)
  const [actionBusy, setActionBusy] = useState(false)
  const mountedRef = useRef(false)
  const actionInFlight = useRef(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setJobs(await getFailedJobs())
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load()
  }, [load])

  const runWithLock = useCallback(
    async (fn: () => Promise<void>, successMessage: string) => {
      if (actionInFlight.current || !jobs.length) return
      actionInFlight.current = true
      setActionBusy(true)
      try {
        await fn()
        showSuccess(successMessage)
        await load()
      } catch (error) {
        showApiError(error)
      } finally {
        actionInFlight.current = false
        setActionBusy(false)
      }
    },
    [jobs.length, load],
  )

  const handleRetryAll = () => {
    void runWithLock(() => retryFailedJob(), 'Job Retry Successfully')
  }

  const handleDeleteAll = () => {
    void runWithLock(() => deleteFailedJob(), 'Job Deleted Successfully')
  }

  const handleRetryOne = (id: number) => {
    if (actionInFlight.current) return
    void (async () => {
      actionInFlight.current = true
      setActionBusy(true)
      try {
        await retryFailedJob(id)
        showSuccess('Job Retry Successfully')
        await load()
      } catch (error) {
        showApiError(error)
      } finally {
        actionInFlight.current = false
        setActionBusy(false)
      }
    })()
  }

  const handleDeleteOne = (id: number) => {
    if (actionInFlight.current) return
    void (async () => {
      actionInFlight.current = true
      setActionBusy(true)
      try {
        await deleteFailedJob(id)
        showSuccess('Job Deleted Successfully')
        await load()
      } catch (error) {
        showApiError(error)
      } finally {
        actionInFlight.current = false
        setActionBusy(false)
      }
    })()
  }

  const controlsDisabled = loading || actionBusy || jobs.length === 0

  return (
    <PageShell title="FAILED JOBS">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to="/jobs/queued"
          className={
            actionBusy
              ? `${jobToolbarClassName} opacity-50 pointer-events-none`
              : jobToolbarClassName
          }
          aria-disabled={actionBusy}
        >
          Queued Jobs
        </Link>
        <button
          type="button"
          onClick={handleRetryAll}
          disabled={controlsDisabled}
          className={jobToolbarClassName}
        >
          Retry All
        </button>
        <button
          type="button"
          onClick={handleDeleteAll}
          disabled={controlsDisabled}
          className={jobToolbarClassName}
        >
          Delete All
        </button>
      </div>

      {actionBusy && (
        <p className="text-sm text-slate-500" role="status">
          Processing…
        </p>
      )}

      <ListingDataTableCard>
        <ListingDataTableScroll>
          <table className={LISTING_DATA_TABLE_CLASS}>
            <thead className={LISTING_DATA_TABLE_HEAD_CLASS}>
              <tr>
                <th className="px-4 py-3 text-left">ID #</th>
                <th className="px-4 py-3 text-left">Queue</th>
                <th className="px-4 py-3 text-left">Time</th>
                <th className="px-4 py-3 text-left">Exception</th>
                <th className="px-4 py-3 text-left">Payload</th>
                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody className={LISTING_DATA_TABLE_BODY_CLASS}>
            {loading ? (
              <ListingTableLoadingRow colSpan={6} />
            ) : jobs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  No failed jobs.
                </td>
              </tr>
            ) : (
              jobs.map((job) => (
                <tr key={job.id} className="align-top bg-white">
                  <td className="px-4 py-3 font-medium text-slate-900">{job.id}</td>
                  <td className="px-4 py-3 text-slate-700">{job.queue}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                    {formatDateTime(job.failed_at)}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <pre className="whitespace-pre-wrap font-mono text-xs max-h-20 overflow-y-auto max-w-xs">
                      {(job.exception || '').split('\n').slice(0, 2).join('\n')}
                    </pre>
                  </td>
                  <td className="px-4 py-3">
                    <textarea
                      readOnly
                      value={job.payload}
                      className="w-56 h-20 font-mono text-xs p-2 border border-slate-200 rounded bg-slate-50"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => handleRetryOne(job.id)}
                        disabled={actionBusy}
                        className="p-1.5 rounded border border-slate-200 bg-white text-[#1597ff] hover:bg-[#1597ff]/10 disabled:opacity-50"
                        aria-label="Retry"
                        title="Retry"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteOne(job.id)}
                        disabled={actionBusy}
                        className="p-1.5 rounded border border-slate-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-50"
                        aria-label="Delete"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </ListingDataTableScroll>
      </ListingDataTableCard>
    </PageShell>
  )
}
